import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import DashboardFilterService from '@/modules/core/dashboard/services/DashboardFilterService';
import DashboardService from '@/modules/ta/dashboard/services/DashboardService';
import WidgetContextFactory from '@/modules/ta/widget/models/context/WidgetContextFactory';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { DashboardEventType } from '@/modules/ta/dashboard/dashboard.constants';
import { ExecutiveSummary } from '@/modules/ta/executivesummaries/models/ExecutiveSummary';
import WidgetService from '@/modules/ta/widget/services/WidgetService';
import { clearDashboardFilterValues } from '@/modules/core/dashboard/utils/DashboardFilterUtilService';
import { WidgetDataCache } from '@/modules/ta/widget/caches/WidgetDataCache';
import { WidgetState } from '@/modules/ta/widget/widget.constants';
import { NotificationProgrammatic as Notification } from 'buefy';
import { i18n } from '@/i18n';

const inFlightLayouts = {}; // layoutId -> Promise

export const actions = {
  /**
   * @param value
   */
  setNeedsReload({}, value) {
    commits.dashboard.setNeedsReload(value);
  },
  /**
   * @param value
   */
  setFiltersNeedsReload({}, value) {
    commits.dashboard.setFiltersNeedsReload(value);
  },
  /**
   * @param value
   */
  setIsInitializingDashboard({}, value) {
    commits.dashboard.setIsInitializingDashboard(value);
  },
  async saveFilters({}, filters) {
    await DashboardFilterService.saveFilters(filters);
    return dispatches.dashboard.setFilters();
  },
  /**
   * @param value
   */
  setFiltersInitialized({}, value) {
    commits.dashboard.setFiltersInitialized(value);
  },
  /**
   * @returns {Promise<*|void>}
   */
  async setFilterOptions({}) {
    return commits.dashboard.setFilterOptions(await DashboardFilterService.getFilterOptions());
  },
  /**
   * @returns {Promise<*|void>}
   */
  async setActiveDashboardFilterOptions({}, dashboardId = null) {
    return commits.dashboard.setActiveDashboardFilterOptions(
      await DashboardFilterService.getFilterOptions(dashboardId)
    );
  },
  async resetActiveDashboardFilterOptions() {
    return commits.dashboard.resetActiveDashboardFilterOptions();
  },
  /**
   * @returns {Promise<*>}
   */
  async setFilters({}, shouldUpdateOldUI = true) {
    if (shouldUpdateOldUI) {
      EventBus.signal(DashboardEventType.DASHBOARD_FILTER_PANEL_OLD_UI_RESET);
    }
    WidgetDataCache.deleteAll();
    return commits.dashboard.setFilters(await DashboardFilterService.getFilters());
  },
  /**
   * @returns {Promise<void>}
   */
  async resetFilters({ state }, clearedFilters = null) {
    EventBus.signal(DashboardEventType.DASHBOARD_FILTER_PANEL_OLD_UI_RESET);
    const dashboardId = state.dashboard?.id ?? null;
    const filtersPayload =
      clearedFilters ??
      clearDashboardFilterValues(state.dashboard?.metadata?.dashboard_filters, dashboardId);

    if (filtersPayload) {
      await DashboardFilterService.saveFilters(filtersPayload);
    } else {
      await DashboardFilterService.resetFilters();
    }
    await dispatches.dashboard.setFilters(false);
  },
  async getDashboard({ state }, { dashboardId, layoutId }) {
    try {
      const dashboard = await DashboardService.getItem(dashboardId, layoutId);
      commits.dashboard.setDashboard({ dashboard });

      // in the case of the leads dashboard that always returns every section we need
      // to pre-populate the hydrated layouts
      Object.values(dashboard?.layouts).forEach((layout) => {
        const widgetIds = Object.keys(layout?.widgets || {});
        if (widgetIds.length) {
          commits.dashboard.addToHydratedLayouts(layout.id);
        } else if (+layoutId === +layout.id) {
          commits.dashboard.addToHydratedLayouts(layout.id);
        }
      });

      return state.dashboard;
    } catch (error) {
      if (error.status === 404) {
        // Extract error message from API error
        // ApiError.message already contains response.data or response.error from the API
        let errorMessage = error.message;

        // Handle case where message is an array
        if (Array.isArray(errorMessage)) {
          [errorMessage] = errorMessage;
        }

        // Fallback to generic message if no message provided
        if (!errorMessage || typeof errorMessage !== 'string') {
          errorMessage = i18n.$t('Item Not Found');
        }

        // Show error notification (indefinite, we'll close it manually)
        const notificationInstance = Notification.open({
          message: errorMessage,
          type: 'is-dark',
          indefinite: true,
        });

        // Close notification after 10 seconds total
        setTimeout(() => {
          if (notificationInstance && notificationInstance.close) {
            notificationInstance.close();
          }
        }, 10000);
      }

      // Re-throw error to propagate to component
      throw error;
    }
  },
  resetShowBenchmarkMessage() {
    commits.dashboard.resetShowBenchmarkMessage();
  },
  resetHydratedLayouts() {
    commits.dashboard.resetHydratedLayouts();
  },
  addToHydratedLayouts({}, layoutId) {
    commits.dashboard.addToHydratedLayouts(layoutId);
  },
  async hydrateDashboardLayout({ state }, layout) {
    const pageId = getters.dashboard.getDashboard().id;

    // If a request for this layout is already in flight, return the same Promise
    if (inFlightLayouts[layout.id]) {
      return inFlightLayouts[layout.id];
    }

    const { dashboard } = state;
    commits.dashboard.updateHydratingLayout(layout.id, true);

    const fetchPromise = (async () => {
      try {
        if (dashboard.executive_summary_enabled) {
          const executiveSummary = layout.executive_summary.id
            ? layout.executive_summary
            : new ExecutiveSummary({ layout_id: layout.id });
          commits.dashboardLayout.setExecutiveSummaryOnLayout(executiveSummary);
        }

        const fetchedDashboard = await DashboardService.getDashboardWithOneLayout(
          pageId,
          layout.id
        );
        const fetchedLayout = fetchedDashboard.layouts[layout.id];
        commits.dashboard.addToHydratedLayouts(layout.id);

        if (Object.keys(fetchedLayout.widgets || []).length) {
          commits.dashboard.hydrateWidgetsInLayout({ layout: fetchedLayout });
          if (!dashboard.layouts[fetchedLayout.id]) {
            dashboard.layouts[fetchedLayout.id] = fetchedLayout;
          }
          dashboard.layouts[fetchedLayout.id].widgets = fetchedLayout.widgets;
          commits.widget.setWidgetsFromLayout({ layout: fetchedLayout });

          // If this layout is the current layout, trigger widget data loading
          const currentLayoutId = getters.dashboard.getCurrentLayoutId();
          if (currentLayoutId === fetchedLayout.id) {
            const widgetIds = Object.keys(fetchedLayout.widgets);
            const widgetStoreModels = getters.widget.getWidgetModelsFromIds(widgetIds);
            widgetStoreModels.forEach((widgetModel) => {
              if (
                widgetModel &&
                (widgetModel.state === WidgetState.STATE_LOADING ||
                  widgetModel.state === WidgetState.STATE_NEEDS_RELOAD)
              ) {
                commits.widget.setState({
                  widget: widgetModel.widget,
                  state: WidgetState.STATE_NEEDS_RELOAD,
                });
              }
            });
          }
        }

        commits.dashboard.updateHydratingLayout(layout, false);
        commits.dashboard.addToHydratedLayouts(layout.id);
        commits.dashboard.setShowBenchmarkMessage(
          (Object.values(fetchedLayout?.widgets) || []).some(
            (widget) => widget && widget.metadata?.data_columns?.benchmarks?.length
          )
        );
        return fetchedLayout;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[ERROR] Failed to hydrate layout ${layout.id}:`, error);
        commits.dashboard.updateHydratingLayout(layout, false);
        throw error;
      } finally {
        // Clear in-flight tracking
        delete inFlightLayouts[layout.id];
      }
    })();

    inFlightLayouts[layout.id] = fetchPromise;
    return fetchPromise;
  },

  async setShowBenchmarkMessage({}, layout) {
    commits.dashboard.setShowBenchmarkMessage(
      (Object.values(layout?.widgets) || []).some(
        (widget) => widget && widget.metadata?.data_columns?.benchmarks?.length
      )
    );
  },

  async getWidgetData({}, { widget, dataService }) {
    const context = WidgetContextFactory.getContext();
    return dispatches.widget.getData({ widget, dataService, context });
  },
  async addLayout({}) {
    // missing layout
    // await dispatches.dashboardLayout.addLayout({ layout: newLayout });
    // commits.dashboard.addLayout({ layout: newLayout });
  },
  setResizingWidget({}, widgetId = null) {
    commits.dashboard.setResizingWidget(widgetId);
  },
  setCurrentLayoutId({ state }, id) {
    commits.dashboard.setCurrentLayoutId(id);
    if (id) {
      const hasPendingStart =
        state.layoutLoadStartLayoutId === null &&
        state.layoutLoadStartEpochMs !== null &&
        state.layoutLoadStartPerfMs !== null;
      const epochMs = hasPendingStart ? state.layoutLoadStartEpochMs : Date.now();
      const fallbackPerfMs = typeof performance !== 'undefined' ? performance.now() : null;
      const perfMs = hasPendingStart ? state.layoutLoadStartPerfMs : fallbackPerfMs;
      commits.dashboard.setLayoutLoadStart({ layoutId: id, epochMs, perfMs });
    } else {
      commits.dashboard.setLayoutLoadStart({
        layoutId: null,
        epochMs: null,
        perfMs: null,
      });
    }
    if (id) {
      const layout = getters.dashboardLayout.getLayout(id);
      commits.executivesummaries.setExecutiveSummaryFromLayout({
        layout,
        fetch: false,
      });

      // NOTE: WidgetDataCache eviction is intentionally skipped to avoid re-fetching data
      // when revisiting sections (max observed size is ~70KB, negligible memory cost).

      // Clear accumulated Performance Timeline entries on every section switch.
      // Vue.config.performance and browser internals add entries that are never evicted.
      window.performance?.clearMeasures?.();
      window.performance?.clearMarks?.();

      const widgets = getters.dashboardLayout.getWidgetsInLayout(id);

      // Trigger data loading for widgets in the newly active layout that are still in STATE_LOADING
      if (widgets && widgets.length) {
        const widgetStoreModels = getters.widget.getWidgetModelsFromIds(widgets.map((w) => w.id));
        widgetStoreModels.forEach((widgetModel) => {
          if (widgetModel && widgetModel.state === WidgetState.STATE_LOADING) {
            commits.widget.setState({
              widget: widgetModel.widget,
              state: WidgetState.STATE_NEEDS_RELOAD,
            });
          }
        });
      }
    }
  },
  seedLayoutLoadStart() {
    const epochMs = Date.now();
    const perfMs = typeof performance !== 'undefined' ? performance.now() : null;
    commits.dashboard.setLayoutLoadStart({
      layoutId: null,
      epochMs,
      perfMs,
    });
  },
  setDashboard({}, dashboard) {
    return commits.dashboard.setDashboard({ dashboard });
  },
  async setExecutiveSummaryEnabled({}, { dashboardId, value }) {
    await DashboardService.setExecutiveSummaryEnabled(dashboardId, value);
    commits.dashboard.setExecutiveSummaryEnabled(value);
  },
  async setHighlightedWidgetsOptions({}, value) {
    commits.dashboard.setHighlightedWidgetsOptions(value);
  },
  async clearHighlightedWidgetsOptions() {
    commits.dashboard.setHighlightedWidgetsOptions([]);
  },
  async fetchMapboxAccessToken({ state }) {
    let accessToken = state.dashboard.mapBoxAccessToken;
    if (!accessToken) {
      const response = await WidgetService.getMapboxAccessToken();
      accessToken = response.token || response.data?.data?.token;
      commits.dashboard.setMapboxAccessToken(accessToken);
    }
    return accessToken;
  },
  async hydrateAllLayout({ state }, dashboard) {
    // Filter out layouts that haven't been hydrated
    const layoutsToHydrate = Object.values(dashboard.layouts).filter(
      (layout) => !state.hydratedLayouts.includes(layout?.id)
    );
    // Hydrate all layouts in parallel using Promise.all
    return Promise.all(
      layoutsToHydrate.map((layout) => dispatches.dashboard.hydrateDashboardLayout(layout))
    );
  },
  cleanup() {
    commits.dashboard.cleanup();
  },
};
